import type { GroupPhoto } from '@lbc/shared';

import type { WrappedStats } from './wrapped-model';
import { WrappedCharts } from './wrapped-charts';
import { personLabel } from './wrapped-display-text';
import { WrappedPhotoHighlights } from './wrapped-photo-highlights';
import { WrappedHero, WrappedRankingRow, WrappedStat } from './wrapped-primitives';

export function GroupWrappedSection({
  stats,
  groupName,
  highlights,
}: {
  stats: WrappedStats;
  groupName: string;
  highlights: GroupPhoto[];
}) {
  const mostDrinks = stats.participantStats[0];
  const leastDrinks = [...stats.participantStats].sort(
    (left, right) => left.drinks - right.drinks,
  )[0];
  const longestStreak = [...stats.participantStats].sort(
    (left, right) => right.longestStreak - left.longestStreak,
  )[0];
  const longestBreak = [...stats.participantStats].sort(
    (left, right) => right.longestBreak - left.longestBreak,
  )[0];

  return (
    <>
      <WrappedHero stats={stats} personal={false} />

      <section className="wrapped-stat-grid" aria-label="Gruppenkennzahlen">
        <WrappedStat
          value={stats.drinkingDays.toString()}
          label="Gemeinsame Trinktage"
          hint="Tage, an denen mindestens eine Person getrunken hat"
        />
        <WrappedStat
          value={stats.noDrinkDays.toString()}
          label="Tage ohne Gruppeneintrag"
          hint="Seit dem ersten Gruppeneintrag bis heute"
        />
        <WrappedStat
          value={personLabel(mostDrinks)}
          label="Meiste Getränke"
          hint={mostDrinks === undefined ? 'Noch keine Daten' : `${mostDrinks.drinks} Getränke`}
        />
        <WrappedStat
          value={personLabel(leastDrinks)}
          label="Wenigste Getränke"
          hint={leastDrinks === undefined ? 'Noch keine Daten' : `${leastDrinks.drinks} Getränke`}
        />
        <WrappedStat
          value={personLabel(longestStreak)}
          label="Längste Serie"
          hint={
            longestStreak === undefined
              ? 'Noch keine Daten'
              : `${longestStreak.longestStreak} Tage in Folge`
          }
        />
        <WrappedStat
          value={personLabel(longestBreak)}
          label="Längste Pause"
          hint={
            longestBreak === undefined ? 'Noch keine Daten' : `${longestBreak.longestBreak} Tage`
          }
        />
        <WrappedStat
          value={stats.strongestMonth?.label ?? '—'}
          label="Stärkster Monat"
          hint={`${stats.strongestMonth?.drinkingDays ?? 0} gemeinsame Trinktage`}
        />
        <WrappedStat
          value={stats.favoriteCategory ?? '—'}
          label="Lieblingsgetränk der Gruppe"
          hint="Häufigster erfasster Getränketyp"
        />
      </section>

      <WrappedCharts
        stats={stats}
        monthlyMetric="drinkingDays"
        monthlyTitle="Gemeinsame Trinktage pro Monat"
      />

      <WrappedPhotoHighlights highlights={highlights} />

      <section
        className="wrapped-panel wrapped-panel--full"
        aria-labelledby="wrapped-ranking-title"
      >
        <div className="section-heading">
          <div>
            <p className="step-label">Die Gruppe im Detail</p>
            <h3 id="wrapped-ranking-title">{groupName}</h3>
          </div>
          <span className="people-count">{stats.participantStats.length} Personen</span>
        </div>
        <div className="wrapped-ranking">
          {stats.participantStats.map((participantStats, index) => (
            <WrappedRankingRow
              key={participantStats.participant.id}
              stats={participantStats}
              rank={index + 1}
            />
          ))}
        </div>
      </section>
    </>
  );
}
